import type { FileSystem } from "@/utils/yjs/FileSystem";
import { WebContainer } from "@webcontainer/api";
import type { Command } from "./WebContainerContext";

export interface TerminalMessage {
	// Content of the message
	content: string;
	// Did the message come from a remote source?
	remote: boolean;
}

export interface Engine {
	instance: WebContainer | null;
	/** Initialize the engine with the initial file system
	 *
	 * @param initialFileSystem - The initial file system to mount.
	 * @returns - A promise that resolves when the engine is initialized.
	 */
	initialize: (initialFileSystem: FileSystem) => Promise<void>;
	/** Given a path, and file contents, write those contents
	 * to the file path.
	 *
	 * @param path - The path to write the file to.
	 * @param newContents - The new contents of the file.
	 * @returns - A promise that resolves when the file is written.
	 */
	writeFile: (path: string, newContents: string) => Promise<void>;
	/** Given a path, create a directory at that path
	 *
	 * @param path - The path to create the directory at.
	 * @returns - A promise that resolves when the directory is created.
	 */
	writeDirectory: (path: string) => Promise<void>;
	/**
	 * Removes a file or directory from the filesystem.
	 *
	 * @param {string} path - The path of the file or directory to remove.
	 */
	rm: (path: string) => void;
	/** Given a command, run it in the engine runtime
	 *
	 * @param command - The command to run.
	 * @returns - A promise that resolves with the exit code of the command.
	 */
	runCommand: (command: Command, stdout: WritableStream) => Promise<number>;
	/** Teardown the engine */
	teardown: () => void;
}

export class WebContainerEngine implements Engine {
	public instance: WebContainer | null = null;

	public async initialize(initialFileSystem: FileSystem) {
		this.instance = await WebContainer.boot();
		await this.instance.mount(initialFileSystem.toFileSystemTree());
	}

	public async writeFile(path: string, newContents: string) {
		return await this.instance?.fs.writeFile(path, newContents);
	}

	public async writeDirectory(path: string) {
		return await this.instance?.fs.mkdir(path);
	}

	public async runCommand(
		command: Command,
		stdout: WritableStream,
	): Promise<number> {
		if (this.instance) {
			const process = await this.instance.spawn(
				command.command,
				command.args,
				command.options,
			);

			// Pipe the output to stdout
			process.output.pipeTo(stdout);

			return process.exit;
		}
		return Promise.resolve(0);
	}

	public teardown() {
		this.instance?.teardown();
	}

	public rm(path: string) {
		this.instance?.fs.rm(path);
	}
}
